/**
 * Notes List Page — mirrors the Contracts list (pages/ContractList.js): header, filter card
 * (search, status, quick-filter chips, investor chips, reset), a server-side paged and sortable
 * table, and row click into the wizard (Draft/Rejected) or the detail page.
 *
 * Module gating: hidden from the sidebar unless tenant_modules('notes') is enabled; the API
 * refuses too. Actions are gated per role on MODULES.NOTES (Role Permission Matrix).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, Divider, FormControl, IconButton, InputLabel, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Typography,
} from '@mui/material';
import {
  Add as AddIcon, RequestQuote as NoteIcon, Delete as DeleteIcon, RestartAlt as RestartAltIcon,
  People as PeopleIcon, Person as PersonIcon, FileDownload as ExportIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiGet, apiDelete, extractApiData, extractApiError } from '../../utils/apiClient';
import { formatNumber } from '../../utils/formatters';
import { exportToExcel } from '../../utils/excelExport';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTenant } from '../../contexts/TenantContext';
import { MODULES } from '../../contexts/PermissionConstants';
import { useNotification } from '../../contexts/NotificationContext';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import usePagination from '../../hooks/usePagination';
import ContractReferenceCell from '../../components/contracts/ContractReferenceCell';
import useTableSort from '../../hooks/useTableSort';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SortableHeaderCell from '../../components/common/SortableHeaderCell';
import { getStatusColor } from '../../utils/statusColors';

const SORTABLE_COLUMNS = {
  reference:        { label: 'Reference',           align: 'left'   },
  investor_name:    { label: 'Investor',            align: 'left'   },
  issuer_name:      { label: 'Issuer',              align: 'left'   },
  investment_type:  { label: 'Investment Type',     align: 'center' },
  profile_name:     { label: 'Calculation Profile', align: 'center' },
  gross_rate:       { label: 'Gross Rate',          align: 'center' },
  issue_date:       { label: 'Start Date',          align: 'center' },
  maturity_date:    { label: 'Maturity Date',       align: 'center' },
  total_nominal:    { label: 'Nominal',             align: 'right'  },
  total_commission: { label: 'Commission',          align: 'right'  },
  status:           { label: 'Status',              align: 'center' },
};
const COLUMN_COUNT = Object.keys(SORTABLE_COLUMNS).length + 1;
const STATUSES = ['Draft', 'Pending Approval', 'Active', 'Matured', 'Rejected', 'Paid'];
const QUICK_STATUSES = ['Draft', 'Pending Approval', 'Active', 'Matured', 'Paid'];

const fmtDate = (d) => (d ? format(new Date(d), 'dd MMM yyyy') : '-');

/** Flatten notes to one row per investor in the workbook's column order, for CSV export. */
export const notesToExportRows = (notes) => notes.flatMap((n) => n.legs.map((l) => {
  const f = l.figures || {};
  const p = l.pricing || {};
  return {
    Code: l.code,
    'System Reference': n.reference,
    'Investment Type': n.investment_type || '',
    Client: l.investor_name,
    Status: n.status,
    'Is Client Staff?': (l.is_staff ?? p.is_staff) ? 'Yes' : 'No',
    'Is Client a Company?': (l.is_company ?? p.is_company) ? 'Yes' : 'No',
    'Int Pay Dates': n.interest_frequency,
    'Nominal Amount': Number(l.nominal_amount),
    'Gross Rate': Number(n.gross_rate),
    'Net Rate': Number(l.client_rate ?? p.client_rate ?? 0),
    'Commission Spread': Number(l.commission_spread ?? p.commission_spread ?? 0),
    'Maturity Date': n.maturity_date,
    'Issue Date': l.issue_date,
    Duration: f.days ?? '',
    'Gross Interest Amount': f.gross_interest ?? '',
    'Net Interest Amount': f.client_interest ?? '',
    Commission: f.commission_amount ?? '',
    WHT: f.wht_amount ?? '',
    'Net Interest Due to Client': f.client_net_due ?? '',
    'Total Due to Client at Maturity': f.total_due ?? '',
    'Gross Maturity Value': f.gross_maturity_value ?? '',
    'Net Maturity Value': f.net_maturity_value ?? '',
    Preview: l.is_preview ? 'Yes' : '',
  };
}));

function NoteList() {
  const navigate = useNavigate();
  const notify = useNotification();
  const confirm = useConfirmDialog();
  const { hasAction } = usePermissions();
  const { isModuleEnabled } = useTenant();
  const enabled = isModuleEnabled(MODULES.NOTES);
  const canCreate = hasAction(MODULES.NOTES, 'create');
  const canDelete = hasAction(MODULES.NOTES, 'delete');

  const pagination = usePagination({ defaultRowsPerPage: 10 });
  const { page, rowsPerPage, resetPage } = pagination;

  // Deeplink support, as on Contracts: /notes?status=Active&sort=maturity_date&dir=asc.
  const [searchParams] = useSearchParams();
  const initialSort = useMemo(() => {
    const col = searchParams.get('sort');
    const dir = searchParams.get('dir');
    return col && SORTABLE_COLUMNS[col] && (dir === 'asc' || dir === 'desc') ? [{ column: col, dir }] : [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initialStatus = useMemo(() => {
    const s = searchParams.get('status');
    return s && STATUSES.includes(s) ? s : 'all';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const sort = useTableSort({ onSortChange: resetPage, defaultSort: initialSort });

  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [investorFilter, setInvestorFilter] = useState('all'); // 'all' | 'multi' | 'single'
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [notes, setNotes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    resetPage();
  }, [statusFilter, investorFilter, debouncedSearch, resetPage]);

  const filterParams = useCallback(() => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (investorFilter === 'multi') params.multi_investor = 'true';
    else if (investorFilter === 'single') params.multi_investor = 'false';
    if (debouncedSearch) params.search = debouncedSearch;
    return params;
  }, [statusFilter, investorFilter, debouncedSearch]);

  const fetchNotes = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const params = { page: page + 1, limit: rowsPerPage, ...filterParams(), ...sort.apiParams };
      const response = await apiGet('/notes', { params });
      setNotes(response?.data || []);
      setTotalCount(response?.pagination?.total || 0);
    } catch (err) {
      setError(extractApiError(err, 'Could not load notes'));
      setNotes([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [enabled, page, rowsPerPage, filterParams, sort.apiParams]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleViewNote = (id, status) => {
    if (status === 'Draft' || status === 'Rejected') navigate(`/notes/${id}/edit`);
    else navigate(`/notes/${id}`);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await apiGet('/notes/export', { params: filterParams() });
      const rows = notesToExportRows(extractApiData(res));
      if (rows.length === 0) { notify.info('Nothing to export'); return; }
      exportToExcel(rows, `notes_${new Date().toISOString().slice(0, 10)}.csv`, 'Notes register');
    } catch (err) {
      notify.error(extractApiError(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const toggleStatus = (s) => setStatusFilter(statusFilter === s ? 'all' : s);
  const anyFilterActive = statusFilter !== 'all' || investorFilter !== 'all' || searchTerm || sort.sorts.length > 0;
  const resetAll = () => {
    setStatusFilter('all');
    setInvestorFilter('all');
    setSearchTerm('');
    sort.reset();
  };

  if (!enabled) {
    return (
      <Alert severity="info">The Notes module is not enabled for this company. An administrator can enable it in Tenant Management.</Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Notes</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport} disabled={exporting || totalCount === 0}>
            {exporting ? 'Exporting…' : 'Export'}
          </Button>
          {canCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/notes/new')}>New Note</Button>
          )}
        </Box>
      </Box>

      {/* Filter bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField label="Search" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 250 }} placeholder="Reference (yours or NTE-…), investor or issuer..." />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="all">All Status</MenuItem>
                {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>

            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {QUICK_STATUSES.map((s) => (
                <Chip key={s} label={s} clickable
                  color={statusFilter === s ? getStatusColor(s) : 'default'}
                  variant={statusFilter === s ? 'filled' : 'outlined'}
                  onClick={() => toggleStatus(s)} />
              ))}
            </Box>

            <Divider orientation="vertical" flexItem />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip icon={<PeopleIcon />} label="Multi-investor" clickable
                color={investorFilter === 'multi' ? 'primary' : 'default'}
                variant={investorFilter === 'multi' ? 'filled' : 'outlined'}
                onClick={() => setInvestorFilter(investorFilter === 'multi' ? 'all' : 'multi')} />
              <Chip icon={<PersonIcon />} label="Single-investor" clickable
                color={investorFilter === 'single' ? 'primary' : 'default'}
                variant={investorFilter === 'single' ? 'filled' : 'outlined'}
                onClick={() => setInvestorFilter(investorFilter === 'single' ? 'all' : 'single')} />
            </Box>

            {anyFilterActive && (
              <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} onClick={resetAll} sx={{ ml: 'auto' }}>
                Reset
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {Object.entries(SORTABLE_COLUMNS).map(([key, col]) => (
                <SortableHeaderCell key={key} sort={sort} columnKey={key} align={col.align}>{col.label}</SortableHeaderCell>
              ))}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 6 }}><LoadingSpinner /></TableCell></TableRow>
            ) : (
              <>
                {notes.map((n) => (
                  <TableRow key={n.id} hover onClick={() => handleViewNote(n.id, n.status)} sx={{ cursor: 'pointer' }}>
                    {/* T3-158: user reference bold, system reference beneath — as contracts show theirs. */}
                    <TableCell><ContractReferenceCell reference={n.reference} userReference={n.user_reference} /></TableCell>
                    <TableCell>
                      {n.investor_count > 1
                        ? <Chip icon={<PeopleIcon />} label={`Multiple (${n.investor_count})`} size="small" variant="outlined" color="primary" />
                        : (n.investor_name || '-')}
                    </TableCell>
                    <TableCell>{n.issuer_name || '-'}</TableCell>
                    <TableCell align="center">{n.investment_type ? <Chip label={n.investment_type} size="small" /> : '-'}</TableCell>
                    <TableCell align="center"><Chip label={n.profile_name} size="small" variant="outlined" /></TableCell>
                    <TableCell align="center">{`${Number(n.gross_rate).toFixed(2)}%`}</TableCell>
                    <TableCell align="center">{fmtDate(n.issue_date)}</TableCell>
                    <TableCell align="center">{fmtDate(n.maturity_date)}</TableCell>
                    <TableCell align="right">{n.currency_code} {formatNumber(n.total_nominal, 2)}</TableCell>
                    <TableCell align="right">
                      {n.total_commission === null || n.total_commission === undefined
                        ? <Typography variant="body2" color="text.secondary">-</Typography>
                        : `${n.currency_code} ${formatNumber(n.total_commission, 2)}`}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <Chip label={n.status} color={getStatusColor(n.status)} size="small" />
                        {n.is_matured && n.status === 'Active' && (
                          <Chip label="Matured" size="small" color="secondary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {n.payout_status === 'Pending Approval' && (
                          <Chip label="Payout pending" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" title="Open Note" onClick={() => handleViewNote(n.id, n.status)}>
                        <NoteIcon fontSize="small" />
                      </IconButton>
                      {canDelete && (n.status === 'Draft' || n.status === 'Rejected') && (
                        <IconButton size="small" color="error" title="Delete Note"
                          onClick={() => confirm.show({
                            title: 'Delete note',
                            message: `Delete ${n.user_reference} (${n.reference}) and its investors? This cannot be undone.`,
                            confirmText: 'Delete',
                            onConfirm: () => apiDelete(`/notes/${n.id}`)
                              .then(() => { notify.success(`${n.user_reference} deleted`); fetchNotes(); })
                              .catch((err) => notify.error(extractApiError(err, 'Delete failed'))),
                          })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {notes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>No notes found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
        <TablePagination {...pagination.tableProps(totalCount)} />
      </TableContainer>
      <ConfirmDialog {...confirm.dialogProps} />
    </Box>
  );
}

export default NoteList;
